import { Box, Link as MuiLink } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { JSX } from "react";
import { Link as RouterLink } from "react-router-dom";

import { Chevron, EyeIcon, EyeOffIcon, FolderIcon, PencilIcon } from "./icons.js";

const sidebarIconBtnSx = {
  display: "grid",
  height: 24,
  width: 24,
  placeItems: "center",
  borderRadius: 0.5,
  border: 0,
  bgcolor: "transparent",
  cursor: "pointer",
  color: "text.disabled",
  transition: "all 150ms",
  "&:hover": { bgcolor: "background.default", color: "text.primary" },
} as const;

export function CategoryRow({
  name,
  count,
  hidden,
  expanded,
  focused,
  onToggle,
  onNameToggle,
  navigateTo,
  canManage = false,
  visible = true,
  toggling = false,
  onToggleVisibility,
  onRequestEdit,
  onRequestAddChild,
  depth = 0,
  leaf = false,
}: {
  name: string;
  count: number;
  hidden: boolean;
  expanded: boolean;
  focused: boolean;
  /** Nesting level — a release under its product, say. Indents the row. */
  depth?: number;
  /** A row with nothing under it (a LIBRARY folder): no chevron, the name
   * link only navigates. */
  leaf?: boolean;
  /** Chevron toggle — flips expansion in place, never navigates. */
  onToggle: () => void;
  /** Folder-name toggle — flips expansion AND lets the link navigate, so it
   * marks the resulting focus change as user-driven (see `toggleFromName`). */
  onNameToggle: () => void;
  navigateTo: string;
  /** Editor on a real (non-Uncategorized) category → show eye + pencil. */
  canManage?: boolean;
  visible?: boolean;
  toggling?: boolean;
  onToggleVisibility?: (next: boolean) => void;
  onRequestEdit?: () => void;
  /** A folder above the deepest tier → a "+" that opens New sub-folder
   * pointed at it. Absent on the deepest tier: nesting stops two levels
   * below the top. */
  onRequestAddChild?: () => void;
}): JSX.Element {
  return (
    <Box
      sx={[
        {
          display: "flex",
          alignItems: "center",
          borderRadius: 1,
          ml: depth * 2,
          transition: "background-color 150ms",
          "&:hover .doc-cat-count, &:focus-within .doc-cat-count": { opacity: 0 },
          "&:hover .doc-cat-manage, &:focus-within .doc-cat-manage": { opacity: 1 },
        },
        focused
          ? { bgcolor: "action.selected", color: "primary.main" }
          : { "&:hover": { bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.6) } },
      ]}
    >
      {leaf ? (
        <Box aria-hidden sx={{ height: 28, width: 24, flexShrink: 0 }} />
      ) : (
        <Box
          component="button"
          type="button"
          aria-label={expanded ? `Collapse ${name}` : `Expand ${name}`}
          aria-expanded={expanded}
          onClick={onToggle}
          sx={{
            display: "grid",
            height: 28,
            width: 24,
            placeItems: "center",
            border: 0,
            bgcolor: "transparent",
            cursor: "pointer",
            color: "text.disabled",
            "&:hover": { color: "text.primary" },
          }}
        >
          <Chevron rotated={expanded} />
        </Box>
      )}
      <MuiLink
        component={RouterLink}
        to={navigateTo}
        aria-current={focused ? "page" : undefined}
        underline="none"
        // Clicking the folder NAME toggles expansion too (not just the tiny
        // chevron) — the affordance operators reach for first, matching every
        // file explorer. The Link still navigates to the newest release; this
        // fires before navigation, so one click both opens the folder in the
        // tree and lands on its docs. (Requested 2026-06-06.) Uses the
        // navigation-aware `onNameToggle` so the re-focus from that navigation
        // doesn't re-expand a folder this same click just collapsed (the
        // close→reopen flicker reported 2026-06-08).
        onClick={leaf ? undefined : onNameToggle}
        sx={[
          {
            display: "flex",
            minWidth: 0,
            flex: 1,
            alignItems: "center",
            gap: 1,
            overflow: "hidden",
            py: 0.5,
            fontSize: 14,
            lineHeight: 1.25,
          },
          focused
            ? { fontWeight: 500, color: "primary.main" }
            : hidden
              ? { color: "text.disabled", "&:hover": { color: "text.primary" } }
              : { color: "text.primary", "&:hover": { color: "text.primary" } },
        ]}
      >
        <FolderIcon open={expanded || leaf} />
        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</Box>
      </MuiLink>

      {/* Right slot — FIXED width so the row can never overflow the
        * 260px rail. Shows the doc count by default; on row hover/focus
        * it cross-fades (no width change, no layout shift) to the eye +
        * pencil manage controls. A HIDDEN category reads from the dimmed
        * name + a persistent eye-off marker — NOT a wide "HIDDEN" pill,
        * which sat inside the flex link and pushed the controls past the
        * border (reported 2026-05-20). */}
      <Box sx={{ position: "relative", display: "flex", height: 28, width: canManage && onRequestAddChild ? 76 : 52, flexShrink: 0, alignItems: "center", justifyContent: "flex-end", pr: 0.75, color: "text.disabled" }}>
        <Box
          component="span"
          className={canManage ? "doc-cat-count" : undefined}
          sx={{ display: "flex", alignItems: "center", transition: "opacity 150ms" }}
        >
          {hidden ? (
            <EyeOffIcon />
          ) : (
            <Box component="span" sx={{ fontSize: "11px", fontVariantNumeric: "tabular-nums" }}>{count}</Box>
          )}
        </Box>

        {canManage && (
          <Box className="doc-cat-manage" sx={{ position: "absolute", insetBlock: 0, right: 2, display: "flex", alignItems: "center", gap: 0.25, opacity: 0, transition: "opacity 150ms" }}>
            {onToggleVisibility && (
              <Box
                component="button"
                type="button"
                aria-label={
                  visible ? `Hide ${name} from customer portal` : `Show ${name} in customer portal`
                }
                aria-pressed={visible}
                disabled={toggling}
                data-testid={`sidebar-cat-visibility-${name}`}
                onClick={() => onToggleVisibility(!visible)}
                title={
                  visible
                    ? "Visible in customer portal — click to hide"
                    : "Hidden from customer portal — click to show"
                }
                sx={[sidebarIconBtnSx, toggling ? { opacity: 0.5 } : {}]}
              >
                {visible ? <EyeIcon /> : <EyeOffIcon />}
              </Box>
            )}
            {onRequestAddChild && (
              <Box
                component="button"
                type="button"
                aria-label={`New sub-folder in ${name}`}
                data-testid={`sidebar-cat-add-sub-${name}`}
                onClick={onRequestAddChild}
                title={`New sub-folder in ${name}`}
                sx={[sidebarIconBtnSx, { fontSize: 16, lineHeight: 1 }]}
              >
                +
              </Box>
            )}
            {onRequestEdit && (
              <Box
                component="button"
                type="button"
                aria-label={`Rename ${name}`}
                data-testid={`sidebar-cat-edit-${name}`}
                onClick={onRequestEdit}
                title={`Rename ${name}`}
                sx={sidebarIconBtnSx}
              >
                <PencilIcon />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
